import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { Button, TextField } from "@mui/material";
import { useCallback, useState } from "react";
import TooltipWrapper from "../../../../../../components/TooltipWrapper.tsx";
import { Maybe } from "../../../../../../types.ts";

interface Props {
  customFieldValue: Maybe<string>;
  originalFieldValue: string;
  resetToDefaultText?: string;
  validationError?: string;
  onApply: (customFieldValue: string) => void;
  onCancel: () => void;
  sanitizeInput?: (customFieldValue: string) => string;
}
export default function EditField({
  customFieldValue,
  originalFieldValue,
  onApply,
  onCancel,
  resetToDefaultText,
  validationError,
  sanitizeInput,
}: Props) {
  const [customLabel, setCustomLabel] = useState(() => {
    const value = customFieldValue ? customFieldValue : originalFieldValue;
    return sanitizeInput ? sanitizeInput(value) : value;
  });

  const handleApply = useCallback(() => {
    if (customLabel) {
      onApply(getCustomLabel(customLabel, originalFieldValue));
    }
  }, [customLabel, onApply, originalFieldValue]);

  return (
    <>
      <TextField
        placeholder={originalFieldValue}
        sx={(theme) => ({
          flex: 1,
          fieldset: {
            border: "none",
          },
          "& .MuiOutlinedInput-root": {
            color: theme.palette.text.primary,
            borderRadius: "4px",
            border: `2px solid ${theme.palette.primary.main}`,
            maxHeight: "30px",
          },
          "& .MuiFormHelperText-root": {
            mt: "-1px",
            position: "absolute",
            left: 0,
            top: "100%",
            textWrap: "nowrap",
            fontSize: "11px",
          },
        })}
        slotProps={{
          input: {
            autoFocus: true,
            value: customLabel,
            onChange: (evt) => {
              const value = evt.target.value;
              setCustomLabel(sanitizeInput ? sanitizeInput(value) : value);
            },
            onKeyDown: (evt) => {
              if (evt.key === "Enter") {
                handleApply();
              }
            },
            endAdornment: (
              <TooltipWrapper title={resetToDefaultText} placement="bottom">
                <RotateLeftIcon
                  onClick={() => setCustomLabel(sanitizeInput ? sanitizeInput(originalFieldValue) : originalFieldValue)}
                  sx={(theme) => ({ cursor: "pointer", color: theme.palette.secondary.light })}
                />
              </TooltipWrapper>
            ),
          },
        }}
        error={!!validationError}
        helperText={validationError}
      />
      <Button variant="outlined" color="secondary" sx={(theme) => ({ maxHeight: theme.spacing(4) })} onClick={onCancel}>
        Cancel
      </Button>
      <Button
        variant="contained"
        sx={(theme) => ({ maxHeight: theme.spacing(4) })}
        disabled={!customLabel}
        onClick={handleApply}
      >
        Apply
      </Button>
    </>
  );
}

function getCustomLabel(customLabel: string | undefined, originalName: string) {
  return customLabel === originalName ? originalName : (customLabel ?? "");
}
